import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { apiClient } from '../api/client';
import { Button, Card, CardContent, CardHeader, CardTitle, LoadingSpinner } from '../components/ui';
import { Download, ArrowLeft, FileText, TrendingUp, Shield, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const Results = () => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const { attemptId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && token) {
      fetchResults();
    }
  }, [user, token, attemptId]);

  const fetchResults = async () => {
    try {
      let response;
      if (attemptId) {
        response = await apiClient.get(`/results/attempt/${attemptId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        response = await apiClient.get(`/results/${user.uid}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setResults(response.data);
    } catch (error) {
      console.error('Failed to fetch results:', error);
      toast.error('Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = async () => {
    if (!attemptId) {
      toast.error('No specific assessment selected for PDF generation');
      return;
    }

    setGeneratingPDF(true);
    try {
      // First, generate the PDF
      await apiClient.post(`/reports/${attemptId}/generate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Then download it
      const downloadResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL}/reports/${attemptId}/file`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (downloadResponse.ok) {
        const blob = await downloadResponse.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `CogniQ-Professional-Report-${attemptId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Professional PDF report downloaded successfully!');
      } else {
        if (downloadResponse.status === 404) {
          toast.error('PDF not found. Please try again.');
        } else {
          toast.error('Failed to download PDF');
        }
      }
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      toast.error(`Failed to generate PDF report: ${error.response?.data?.error || error.message}`);
    } finally {
      setGeneratingPDF(false);
    }
  };

  const getLevel = (score) => {
    if (score < 30) return 'Low';
    if (score < 50) return 'Moderate';
    if (score < 70) return 'High';
    return 'Very High';
  };

  const getLevelColor = (score) => {
    if (score < 30) return 'text-red-600';
    if (score < 50) return 'text-orange-600';
    if (score < 70) return 'text-blue-600';
    return 'text-green-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center">
        <motion.div 
          className="text-center"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
        >
          <LoadingSpinner size="xl" className="mb-6" />
          <motion.h2 
            className="text-2xl font-bold text-gray-800 mb-4"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Loading Results
          </motion.h2>
          <motion.p 
            className="text-gray-600"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            Analyzing your assessment data...
          </motion.p>
        </motion.div>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">📊</div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">No Results Found</h2>
        <p className="text-gray-600 mb-6">You haven't completed any assessments yet.</p>
        <button
          onClick={() => navigate('/dashboard')}
          className="bg-violet-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-violet-700 transition-colors"
        >
          Take Your First Assessment
        </button>
      </div>
    );
  }

  // Handle single attempt result
  if (results.attemptId) {
    const result = results;
    
    return (
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">Assessment Results</h2>
          <div className="flex gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-blue-600 hover:text-blue-800 font-semibold"
            >
              Back to Dashboard
            </button>
            <Button
              onClick={generatePDF}
              disabled={generatingPDF}
              loading={generatingPDF}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 flex items-center space-x-2"
              size="lg"
            >
              <Download className="w-4 h-4" />
              <span>{generatingPDF ? 'Generating...' : 'Generate PDF Report'}</span>
            </Button>
          </div>
        </div>

        {/* Security Header */}
        <div className="bg-red-600 text-white p-4 rounded-xl text-center">
          <div className="flex items-center justify-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <strong>CONFIDENTIAL DOCUMENT</strong> - For Authorized Personnel Only
          </div>
        </div>

        {/* BIG FIVE Results */}
        {result.bigFive && (
          <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
            <h3 className="text-xl font-bold mb-6">BIG FIVE Personality Dimensions</h3>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              {Object.entries(result.bigFive.scores).map(([trait, data]) => (
                <div key={trait} className="text-center">
                  <div className="relative w-24 h-24 mx-auto mb-4">
                    <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="45" fill="none" stroke="#e5e7eb" strokeWidth="8"></circle>
                      <circle
                        cx="50"
                        cy="50"
                        r="45"
                        fill="none"
                        stroke="#8b5cf6"
                        strokeWidth="8"
                        strokeDasharray={`${(data.standardized/100) * 283} 283`}
                        strokeLinecap="round"
                      ></circle>
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-2xl font-bold text-gray-800">{data.standardized}</span>
                    </div>
                  </div>
                  <h4 className="font-bold text-gray-800 mb-2 capitalize">{trait}</h4>
                  <div className={`text-sm font-semibold ${getLevelColor(data.standardized)}`}>
                    {data.level}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">P{data.percentile}</div>
                </div>
              ))}
            </div>

            {/* Interpretations */}
            {result.bigFive.interpretations && (
              <div className="mt-8 space-y-4">
                <h4 className="font-bold text-lg">Personality Insights</h4>
                {Object.entries(result.bigFive.interpretations).map(([trait, interpretation]) => (
                  <div key={trait} className="p-4 bg-gray-50 rounded-xl">
                    <h5 className="font-semibold capitalize mb-2">{trait}</h5>
                    <p className="text-gray-700 text-sm">{interpretation}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Productivity Results */}
        {result.productivity && (
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl p-8">
            <h3 className="text-xl font-bold mb-6">Productivity Analysis</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-4xl font-bold text-emerald-600 mb-2">
                  {result.productivity.overallScore}
                </div>
                <div className="text-gray-600">Overall Score</div>
                <div className="text-sm text-gray-500">P{result.productivity.percentile}</div>
              </div>
              <div className="text-center">
                <div className={`text-2xl font-bold mb-2 ${getLevelColor(result.productivity.overallScore)}`}>
                  {result.productivity.level}
                </div>
                <div className="text-gray-600">Performance Level</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600 mb-2">
                  {result.productivity.percentile}th
                </div>
                <div className="text-gray-600">Percentile</div>
              </div>
            </div>
          </div>
        )}

        {/* Recommendations */}
        {result.recommendations && (
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
            <h4 className="font-bold text-blue-800 mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Personalized Recommendations
            </h4>
            
            <div className="space-y-4">
              <div>
                <h5 className="font-semibold mb-2">Work Style</h5>
                <p className="text-gray-700">{result.recommendations.workStyle}</p>
              </div>
              
              <div>
                <h5 className="font-semibold mb-2">Optimal Environment</h5>
                <p className="text-gray-700">{result.recommendations.optimalEnvironment}</p>
              </div>
              
              {result.recommendations.strengths && (
                <div>
                  <h5 className="font-semibold mb-2">Key Strengths</h5>
                  <ul className="list-disc list-inside space-y-1">
                    {result.recommendations.strengths.map((strength, index) => (
                      <li key={index} className="text-gray-700">{strength}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {result.recommendations.improvementAreas && (
                <div>
                  <h5 className="font-semibold mb-2">Development Areas</h5>
                  <ul className="list-disc list-inside space-y-1">
                    {result.recommendations.improvementAreas.map((area, index) => (
                      <li key={index} className="text-gray-700">{area}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Handle multiple results
  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Your Assessment History</h2>
        <button
          onClick={() => navigate('/dashboard')}
          className="text-blue-600 hover:text-blue-800 font-semibold"
        >
          Back to Dashboard
        </button>
      </div>

      {results.assessments && results.assessments.length > 0 ? (
        <div className="space-y-6">
          {results.assessments.map((assessment, index) => (
            <div key={index} className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold capitalize">
                    {assessment.testType} Assessment
                  </h3>
                  <p className="text-gray-600">
                    Completed on {new Date(assessment.completedAt).toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={() => navigate(`/results/${assessment.attemptId}`)}
                  className="text-violet-600 hover:text-violet-700 font-semibold"
                >
                  View Details →
                </button>
              </div>
              
              {assessment.bigFive && (
                <div className="grid grid-cols-5 gap-4 mb-4">
                  {Object.entries(assessment.bigFive.scores).map(([trait, data]) => (
                    <div key={trait} className="text-center">
                      <div className="text-2xl font-bold text-violet-600">{data.standardized}</div>
                      <div className="text-xs text-gray-600 capitalize">{trait}</div>
                    </div>
                  ))}
                </div>
              )}
              
              {assessment.productivity && (
                <div className="text-center">
                  <div className="text-2xl font-bold text-emerald-600">
                    {assessment.productivity.overallScore}
                  </div>
                  <div className="text-sm text-gray-600">Productivity Score</div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📊</div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">No Assessments Yet</h3>
          <p className="text-gray-600 mb-6">Take your first assessment to see results here.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-violet-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-violet-700 transition-colors"
          >
            Start Assessment
          </button>
        </div>
      )}
    </div>
  );
};

export default Results;